import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { useMemo } from "react";
import { Skeleton } from "../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Props = {
  totalPages: number;
  siblingsCount?: number;
  className?: string;
  currentPage: number;
  onChange: (page: number) => void;
  showSizeSelector?: boolean;
  rowsPerPage?: number;
  onRowsPerPageChange?: (limit: number) => void;
  totalEntries?: number;
};

const DOTS = "..." as const;

const range = (start: number, end: number): number[] => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

const CustomPagination = ({
  totalPages,
  siblingsCount = 1,
  className,
  currentPage,
  onChange,
  rowsPerPage,
  onRowsPerPageChange,
  showSizeSelector = true,
  totalEntries,
}: Props) => {
  const paginationRange = useMemo(() => {
    const totalPageNumbers = siblingsCount * 2 + 3;

    if (totalPageNumbers >= totalPages) {
      return range(1, totalPages);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingsCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingsCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingsCount;
      const leftRange = range(1, leftItemCount);
      return [...leftRange, DOTS, totalPages];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingsCount;
      const rightRange = range(totalPages - rightItemCount + 1, totalPages);
      return [1, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [1, DOTS, ...middleRange, DOTS, totalPages];
    }

    return [];
  }, [currentPage, totalPages, siblingsCount]);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  const handlePaginationClick = (pageNumber: number | string) => {
    onChange(Number(pageNumber));
  };

  const renderPaginationItem = (pageNumber: number | string, index: number) => {
    if (pageNumber === DOTS) {
      return (
        <PaginationItem key={`dots-${index}`}>
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    return (
      <PaginationItem key={pageNumber}>
        <PaginationLink
          onClick={() => {
            handlePaginationClick(pageNumber);
          }}
          isActive={pageNumber === currentPage}
          className='size-8 [&[aria-current="page"]]:text-primary cursor-pointer'
        >
          {pageNumber}
        </PaginationLink>
      </PaginationItem>
    );
  };

  // if (totalPages <= 1) return null;

  const renderTotalEntries = () => {
    if (totalEntries && rowsPerPage) {
      const start = (currentPage - 1) * rowsPerPage + 1;
      const end = Math.min(currentPage * rowsPerPage, totalEntries);
      return (
        <span className="text-sm">
          Showing <span className="font-semibold">{start}</span>-
          <span className="font-semibold">{end}</span> of{" "}
          <span className="font-semibold">{totalEntries}</span>
        </span>
      );
    }
    return null;
  };

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row w-full items-center justify-end px-2 py-1",
        showSizeSelector && "justify-between"
      )}
    >
      {showSizeSelector && (
        <div className="flex items-center w-full gap-2">
          <span className="text-sm text-muted-foreground">Rows per page:</span>
          <Select
            value={rowsPerPage?.toString()}
            onValueChange={(value) => onRowsPerPageChange?.(Number(value))}
          >
            <SelectTrigger className="w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
              <SelectItem value="500">500</SelectItem>
            </SelectContent>
          </Select>

          {renderTotalEntries()}
        </div>
      )}

      <Pagination className={cn("justify-center sm:justify-end", className)}>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => {
                handlePaginationClick(currentPage - 1);
              }}
              className={`[&>span]:hidden p-0 size-8 cursor-pointer ${
                isFirstPage ? "pointer-events-none opacity-50" : ""
              }`}
            />
          </PaginationItem>

          {paginationRange.map(renderPaginationItem)}

          <PaginationItem>
            <PaginationNext
              onClick={() => {
                handlePaginationClick(currentPage + 1);
              }}
              className={`[&>span]:hidden p-0 size-8 cursor-pointer ${
                isLastPage ? "pointer-events-none opacity-50" : ""
              }`}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export const PaginationStyledFallback = () => {
  return (
    <div className="flex items-center justify-center gap-2">
      <Skeleton className="size-8" />
      <Skeleton className="size-8" />
      <Skeleton className="size-8" />
      <Skeleton className="size-8" />
    </div>
  );
};

export default CustomPagination;
